import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'

import { ApiError } from '../api/client'
import { PageHeader } from '../components/Page'
import { endpoints, type BusinessType, type CashierSettings } from '../api/endpoints'
import { MAX_PRESET_LENGTH, MAX_PRESETS, PROFILE_DEFAULTS, profileChanges } from '../kasir'
import { formatPercent, formatRupiah, parsePercentBps, parseRupiah } from '../money'
import { priceCheck } from '../pricing'
import { errorMessage, t } from '../strings'

const MAX_RATE_BPS = 2500
const MAX_FEE = 100_000
const typeName = (b: BusinessType) => (b === 'food' ? t.businessFood : t.businessServices)

/** A setting's value in words, for the "what will change" list. */
function describe(key: keyof typeof PROFILE_DEFAULTS.food, value: CashierSettings[keyof CashierSettings]): string {
  if (typeof value === 'boolean') return value ? t.kasirOn : t.kasirOff
  if (key === 'tax_rate_bps' || key === 'service_rate_bps') return `${formatPercent(Number(value))}%`
  if (key === 'takeaway_fee_amount') return formatRupiah(Number(value))
  if (key === 'rounding_unit') return value === 100 ? 'Rp 100' : t.kasirOff
  if (Array.isArray(value)) return value.length === 0 ? t.kasirOff : value.join(', ')
  return String(value)
}

export function KasirSettingsPage() {
  const settings = useQuery({ queryKey: ['cashier-settings'], queryFn: endpoints.cashierSettings })
  return (
    <>
      <PageHeader title={t.kasirTitle} subtitle={t.kasirSub} back={{ to: '/pengaturan', label: t.navSettings }} />
      {settings.isError && <p className="error">{errorMessage(settings.error.message)}</p>}
      {!settings.data && !settings.isError && <p>{t.loading}</p>}
      {settings.data && <KasirForm initial={settings.data} />}
    </>
  )
}

function Switch({ label, hint, checked, onChange }: { label: string; hint?: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="setting">
      <label className="check">
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
        {label}
      </label>
      {hint && <p className="muted small">{hint}</p>}
    </div>
  )
}

function KasirForm({ initial }: { initial: CashierSettings }) {
  const qc = useQueryClient()
  const [s, setS] = useState(initial)
  const [taxText, setTaxText] = useState(formatPercent(initial.tax_rate_bps))
  const [serviceText, setServiceText] = useState(formatPercent(initial.service_rate_bps))
  const [feeText, setFeeText] = useState(initial.takeaway_fee_amount === 0 ? '' : String(initial.takeaway_fee_amount))
  const [newPreset, setNewPreset] = useState('')
  const [confirming, setConfirming] = useState<BusinessType | null>(null)
  const [saved, setSaved] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)

  const taxBps = parsePercentBps(taxText)
  const serviceBps = parsePercentBps(serviceText)
  const fee = feeText.trim() === '' ? 0 : parseRupiah(feeText)
  const taxBad = taxBps === null || taxBps > MAX_RATE_BPS
  const serviceBad = serviceBps === null || serviceBps > MAX_RATE_BPS
  const feeBad = fee === null || fee > MAX_FEE
  const invalid = taxBad || serviceBad || feeBad

  function adopt(next: CashierSettings) {
    qc.setQueryData(['cashier-settings'], next)
    setS(next)
    setTaxText(formatPercent(next.tax_rate_bps))
    setServiceText(formatPercent(next.service_rate_bps))
    setFeeText(next.takeaway_fee_amount === 0 ? '' : String(next.takeaway_fee_amount))
  }

  const fail = (e: unknown) => setFailure(errorMessage(e instanceof ApiError ? e.code : 'network'))
  const save = useMutation({
    mutationFn: endpoints.saveCashierSettings,
    onSuccess: (next) => {
      adopt(next)
      setSaved(true)
    },
    onError: fail,
  })
  const apply = useMutation({
    mutationFn: endpoints.applyProfile,
    onSuccess: (next) => {
      adopt(next)
      setConfirming(null)
      setSaved(true)
    },
    onError: fail,
  })

  const set = <K extends keyof CashierSettings>(k: K, v: CashierSettings[K]) => {
    setSaved(false)
    setFailure(null)
    setS((cur) => ({ ...cur, [k]: v }))
  }

  const preview = priceCheck(
    48_000,
    0,
    0,
    s.service_enabled && serviceBps !== null ? serviceBps : 0,
    s.tax_enabled && taxBps !== null ? taxBps : 0,
    s.prices_include_tax,
    s.rounding_unit,
  )

  function submit(e: FormEvent) {
    e.preventDefault()
    if (invalid || taxBps === null || serviceBps === null || fee === null) return
    setFailure(null)
    const { business_type: _type, ...rest } = s
    save.mutate({ ...rest, tax_rate_bps: taxBps, service_rate_bps: serviceBps, takeaway_fee_amount: fee })
  }

  function addPreset() {
    const p = newPreset.trim()
    if (p === '' || p.length > MAX_PRESET_LENGTH || s.note_presets.length >= MAX_PRESETS) return
    if (!s.note_presets.includes(p)) set('note_presets', [...s.note_presets, p])
    setNewPreset('')
  }

  const other: BusinessType = s.business_type === 'food' ? 'services' : 'food'
  const changes = confirming ? profileChanges(s, confirming) : []

  return (
    <form className="kasir-form" onSubmit={submit}>
      <div className="stack">
      <section className="card">
        <h2>{t.kasirBusinessSection}</h2>
        <p>{t.kasirBusinessNow(typeName(s.business_type))}</p>
        <div className="grid2">
          <button type="button" className="secondary" onClick={() => setConfirming(s.business_type)}>
            {t.kasirApplyProfile}
          </button>
        </div>
        <div className="grid2">
          <button type="button" className="secondary" onClick={() => setConfirming(other)}>
            {t.kasirSwitchTo(typeName(other))}
          </button>
        </div>
        {confirming && (
          <div className="warning" role="alertdialog" aria-label={t.kasirApplyConfirmTitle(typeName(confirming))}>
            <strong>{t.kasirApplyConfirmTitle(typeName(confirming))}</strong>
            {changes.length === 0 ? (
              <p>{t.kasirApplyNothing}</p>
            ) : (
              <>
                <p>{t.kasirApplyConfirmBody}</p>
                <ul>
                  {changes.map((k) => (
                    <li key={k}>
                      {t.kasirFieldNames[k]}: {describe(k, s[k])} → {describe(k, PROFILE_DEFAULTS[confirming][k])}
                    </li>
                  ))}
                </ul>
              </>
            )}
            <div className="grid2">
              <button type="button" disabled={apply.isPending} onClick={() => apply.mutate(confirming)}>
                {t.kasirApplyYes}
              </button>
              <button type="button" className="secondary" onClick={() => setConfirming(null)}>
                {t.kasirApplyNo}
              </button>
            </div>
          </div>
        )}
      </section>

      <section className="card">
        <h2>{t.kasirOrderSection}</h2>
        <Switch label={t.kasirOrderType} checked={s.order_type_enabled} onChange={(v) => set('order_type_enabled', v)} />
        {s.order_type_enabled && (
          <label>
            {t.kasirTakeawayFee}
            <input inputMode="numeric" value={feeText} onChange={(e) => (setSaved(false), setFeeText(e.target.value))} aria-invalid={feeBad} />
            {feeBad && <span className="error small">{t.kasirTakeawayFeeInvalid}</span>}
          </label>
        )}
        <Switch label={t.kasirTable} checked={s.table_number_enabled} onChange={(v) => set('table_number_enabled', v)} />
        <Switch label={t.kasirItemNotes} checked={s.item_notes_enabled} onChange={(v) => set('item_notes_enabled', v)} />
        {s.item_notes_enabled && (
          <div className="setting">
            <div className="small">
              <strong>{t.kasirPresets}</strong>
            </div>
            <div className="chips">
              {s.note_presets.map((p) => (
                <span key={p} className="chip on">
                  {p}
                  <button
                    type="button"
                    className="chip-x"
                    aria-label={t.kasirPresetRemove(p)}
                    onClick={() =>
                      set(
                        'note_presets',
                        s.note_presets.filter((x) => x !== p),
                      )
                    }
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
            {s.note_presets.length < MAX_PRESETS && (
              <div className="row">
                <input
                  aria-label={t.kasirPresetNew}
                  placeholder={t.kasirPresetNew}
                  maxLength={MAX_PRESET_LENGTH}
                  value={newPreset}
                  onChange={(e) => setNewPreset(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      addPreset()
                    }
                  }}
                />
                <button type="button" className="secondary" onClick={addPreset}>
                  {t.kasirPresetAdd}
                </button>
              </div>
            )}
            <p className="muted small">{t.kasirPresetLimit}</p>
          </div>
        )}
      </section>

      <section className="card">
        <h2>{t.kasirPrintSection}</h2>
        <Switch label={t.kasirKitchen} checked={s.kitchen_ticket_enabled} onChange={(v) => set('kitchen_ticket_enabled', v)} />
        {s.kitchen_ticket_enabled && (
          <label>
            {t.kasirKitchenCopies}
            <select value={s.kitchen_ticket_copies} onChange={(e) => set('kitchen_ticket_copies', Number(e.target.value))}>
              {[1, 2, 3].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        )}
        <Switch label={t.kasirReceiptNotes} checked={s.receipt_show_notes} onChange={(v) => set('receipt_show_notes', v)} />
      </section>
      </div>
      <div className="stack">
      <section className="card">
        <h2>{t.kasirTaxSection}</h2>
        <Switch label={t.kasirTaxEnabled} checked={s.tax_enabled} onChange={(v) => set('tax_enabled', v)} />
        {s.tax_enabled && (
          <label>
            {t.kasirTaxRate}
            <input inputMode="decimal" value={taxText} onChange={(e) => (setSaved(false), setTaxText(e.target.value))} aria-invalid={taxBad} />
            {taxBad && <span className="error small">{t.kasirRateInvalid}</span>}
          </label>
        )}
        <Switch label={t.kasirServiceEnabled} checked={s.service_enabled} onChange={(v) => set('service_enabled', v)} />
        {s.service_enabled && (
          <label>
            {t.kasirServiceRate}
            <input
              inputMode="decimal"
              value={serviceText}
              onChange={(e) => (setSaved(false), setServiceText(e.target.value))}
              aria-invalid={serviceBad}
            />
            {serviceBad && <span className="error small">{t.kasirRateInvalid}</span>}
          </label>
        )}
        <Switch label={t.kasirInclusive} hint={t.kasirInclusiveHint} checked={s.prices_include_tax} onChange={(v) => set('prices_include_tax', v)} />
        <Switch label={t.kasirRounding} checked={s.rounding_unit === 100} onChange={(v) => set('rounding_unit', v ? 100 : 0)} />
        <div className="preview" aria-live="polite">
          <strong>{t.kasirPreviewTitle}</strong>
          <div data-testid="preview">
            {t.kasirPreview(
              formatRupiah(preview.service),
              formatRupiah(preview.tax),
              formatRupiah(preview.rounding),
              formatRupiah(preview.net),
            )}
          </div>
        </div>
      </section>

      <section className="card">
        <h2>{t.kasirStaffSection}</h2>
        <Switch label={t.kasirStaffPerLine} checked={s.staff_per_line_enabled} onChange={(v) => set('staff_per_line_enabled', v)} />
      </section>

      <section className="card">
        <h2>{t.kasirOpenBillSection}</h2>
        <Switch label={t.kasirOpenBill} hint={t.kasirOpenBillHint} checked={s.open_bill_enabled} onChange={(v) => set('open_bill_enabled', v)} />
      </section>
      </div>

      <div className="save-bar">
        <button type="submit" className="cta" disabled={save.isPending || invalid}>
          {t.kasirSave}
        </button>
        {saved && (
          <p className="ok" role="status">
            {t.kasirSaved}
          </p>
        )}
        {failure && (
          <p className="error" role="alert">
            {failure}
          </p>
        )}
      </div>
    </form>
  )
}
